import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d6io6hhbfjp31emak9u0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        alert('Thank you for subscribing!');
        form.reset();
      } else {
        alert('Something went wrong. Please try again.');
      }
    } catch (error) {
      alert('Failed to submit. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F7F5]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/85 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-[1400px] mx-auto px-20 py-10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/143640c0-1757-47e0-9e0e-0caaa4c4f009.png" alt="BASE d.o.o." className="h-10 w-10 object-contain" />
            <span className="text-2xl font-medium tracking-wide">BASE d.o.o.</span>
          </div>
          <div className="flex items-center gap-8">
            <button onClick={() => scrollToSection('home')} className="text-base font-semibold hover:text-[#FF6B58] transition-colors cursor-pointer whitespace-nowrap">Home</button>
            <button onClick={() => scrollToSection('about')} className="text-base font-semibold hover:text-[#FF6B58] transition-colors cursor-pointer whitespace-nowrap">About</button>
            <button onClick={() => scrollToSection('products')} className="text-base font-semibold hover:text-[#FF6B58] transition-colors cursor-pointer whitespace-nowrap">Products</button>
            <button onClick={() => scrollToSection('contact')} className="text-base font-semibold hover:text-[#FF6B58] transition-colors cursor-pointer whitespace-nowrap">Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center justify-center relative bg-gradient-to-b from-[#F8F7F5] to-white pt-24">
        <div className="max-w-[1400px] w-full mx-auto px-20">
          <div className="text-[#FF6B58] text-xs uppercase tracking-[2px] font-semibold mb-12">iOS Development Experts</div>
          <div className="max-w-[60%]">
            <h1 className="text-[72px] leading-[1.1] tracking-tight font-bold mb-2">Crafting Premium</h1>
            <h1 className="text-[84px] leading-[1.1] tracking-tight font-normal italic text-[#B8A8E8]" style={{ fontFamily: 'Playfair Display, serif' }}>iOS Experiences</h1>
            <p className="text-[22px] text-[#6B6B6B] max-w-[480px] leading-relaxed mt-8">Specialized in VPN and Cleaner applications that prioritize security and seamless user experience</p>
          </div>
          <div className="mt-20 mx-20">
            <div className="rounded-[32px] overflow-hidden shadow-[0_40px_80px_rgba(0,0,0,0.08)] h-[55vh] bg-gradient-to-br from-[#FF6B58]/10 to-[#B8A8E8]/10 flex items-center justify-center">
              <img src="https://public.readdy.ai/ai/img_res/f8aa4f2e42ea3cef3d1de6f153ffae56.jpg" alt="iOS Applications" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="min-h-screen bg-white flex items-center">
        <div className="max-w-[1400px] w-full mx-auto px-20 py-32 grid grid-cols-5 gap-16">
          <div className="col-span-2 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-12">
              <div className="w-3 h-3 rounded-full bg-[#FF6B58]"></div>
              <span className="text-sm font-semibold">About Us</span>
            </div>
            <div className="max-w-[85%]">
              <div className="text-[120px] leading-none text-[#B8A8E8] font-serif mb-4">"</div>
              <p className="text-[56px] leading-[1.3] font-medium mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>We are BASE d.o.o., a dedicated team transforming mobile security through innovative iOS solutions</p>
              <div className="flex items-center gap-3">
                <span className="text-lg font-semibold">— BASE Team</span>
                <div className="flex items-center gap-2 bg-green-50 px-3 py-1 rounded-full">
                  <i className="ri-checkbox-circle-fill text-green-600 text-sm"></i>
                  <span className="text-sm text-green-700">Verified Developer</span>
                </div>
              </div>
            </div>
          </div>
          <div className="col-span-3">
            <img src="https://readdy.ai/api/search-image?query=professional%20software%20development%20team%20workspace%20from%20elevated%20angle%2C%20modern%20office%20with%20iOS%20devices%20and%20MacBooks%20on%20desk%2C%20black%20and%20white%20photography%2C%20clean%20minimalist%20aesthetic%2C%20developers%20collaborating%20on%20mobile%20applications%2C%20natural%20lighting%2C%20architectural%20perspective&width=900&height=800&seq=about-team-workspace-001&orientation=portrait" alt="BASE Team Workspace" className="w-full h-full object-cover rounded-[24px]" />
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="bg-[#F0F4F1] py-40 px-20">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex justify-between items-start mb-32">
            <h2 className="text-[68px] leading-[1.1] font-bold max-w-[500px]" style={{ fontFamily: 'Playfair Display, serif' }}>Our Products</h2>
            <p className="text-lg text-[#6B6B6B] max-w-[360px] mt-4">VPN and Cleaner applications designed for modern iOS users</p>
          </div>
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-5 bg-white rounded-[24px] p-12 flex flex-col">
              <div className="flex-1 flex items-center justify-center mb-8">
                <div className="w-48 h-48 rounded-[32px] bg-gradient-to-br from-[#FF6B58] to-[#FF8A7A] flex items-center justify-center shadow-lg">
                  <img src="https://readdy.ai/api/search-image?query=modern%20VPN%20application%20icon%20design%20for%20iOS%2C%20shield%20with%20lock%20symbol%2C%20gradient%20coral%20pink%20colors%2C%20minimalist%20flat%20design%2C%20rounded%20square%20app%20icon%2C%20professional%20mobile%20interface%2C%20clean%20white%20background%2C%20security%20theme&width=400&height=400&seq=vpn-app-icon-001&orientation=squarish" alt="VPN App" className="w-40 h-40 object-contain" />
                </div>
              </div>
              <h3 className="text-[32px] font-bold mb-4">VPN Application</h3>
              <p className="text-base text-[#6B6B6B] leading-relaxed">Secure, fast, and private browsing experience with military-grade encryption</p>
            </div>
            <div className="col-span-4 bg-white rounded-[24px] p-10 flex flex-col">
              <h3 className="text-[28px] font-bold mb-6 text-center">Cleaner App</h3>
              <div className="flex-1 flex items-center justify-center">
                <img src="https://readdy.ai/api/search-image?query=iOS%20cleaner%20application%20interface%20screenshot%20showing%20storage%20optimization%20UI%2C%20clean%20modern%20mobile%20app%20design%2C%20storage%20bars%20and%20file%20management%2C%20minimalist%20interface%20with%20pastel%20colors%2C%20rounded%20corners%2C%20professional%20app%20mockup%20on%20white%20background&width=400&height=500&seq=cleaner-app-screen-001&orientation=portrait" alt="Cleaner App" className="w-full h-full object-contain rounded-2xl" />
              </div>
            </div>
            <div className="col-span-3 bg-[#E8E3F5] rounded-[24px] p-10 flex flex-col items-center justify-center">
              <h3 className="text-[28px] font-bold mb-8 text-center">Coming Soon</h3>
              <div className="w-32 h-32 rounded-full bg-white/50 flex items-center justify-center">
                <i className="ri-rocket-line text-6xl text-[#B8A8E8]"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative h-[85vh] overflow-hidden">
        <img src="https://readdy.ai/api/search-image?query=modern%20minimalist%20workspace%20panoramic%20view%20with%20iOS%20devices%20on%20clean%20white%20desk%2C%20MacBook%20and%20iPhone%20showing%20app%20interfaces%2C%20natural%20window%20lighting%2C%20professional%20office%20interior%2C%20soft%20blue%20purple%20ambient%20lighting%2C%20wide%20angle%20photography%2C%20contemporary%20technology%20workspace%20aesthetic&width=1600&height=900&seq=cta-workspace-panorama-001&orientation=landscape" alt="Workspace" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/30 to-purple-900/30"></div>
        <div className="absolute inset-0 flex flex-col justify-between p-20 max-w-[1400px] mx-auto">
          <div className="mt-[15%] ml-20 max-w-[680px]">
            <h2 className="text-[72px] leading-[1.1] font-bold text-white lowercase">ready to elevate your ios experience</h2>
          </div>
          <div className="flex justify-between items-end">
            <div className="w-60 h-40 rounded-2xl bg-gradient-to-br from-[#FF6B58] to-[#B8A8E8] flex items-center justify-center">
              <i className="ri-app-store-fill text-white text-6xl"></i>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-[#2A2A2A] mx-10 mb-10 rounded-t-[40px] px-20 py-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex justify-between items-center mb-20">
            <div className="flex items-center gap-3">
              <img src="https://public.readdy.ai/ai/img_res/143640c0-1757-47e0-9e0e-0caaa4c4f009.png" alt="BASE d.o.o." className="h-8 w-8 object-contain brightness-0 invert" />
              <span className="text-white text-xl">BASE d.o.o.</span>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-16 mb-24">
            <div>
              <h3 className="text-lg text-white font-semibold mb-6">Products</h3>
              <ul className="space-y-4">
                <li><a href="#" className="text-[15px] text-[#9A9A9A] hover:text-[#FF6B58] hover:underline transition-colors cursor-pointer">VPN Application</a></li>
                <li><a href="#" className="text-[15px] text-[#9A9A9A] hover:text-[#FF6B58] hover:underline transition-colors cursor-pointer">Cleaner App</a></li>
                <li><a href="#" className="text-[15px] text-[#9A9A9A] hover:text-[#FF6B58] hover:underline transition-colors cursor-pointer">Feature Updates</a></li>
                <li><a href="#" className="text-[15px] text-[#9A9A9A] hover:text-[#FF6B58] hover:underline transition-colors cursor-pointer">Roadmap</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg text-white font-semibold mb-6">Company</h3>
              <ul className="space-y-4">
                <li><a href="#about" className="text-[15px] text-[#9A9A9A] hover:text-[#FF6B58] hover:underline transition-colors cursor-pointer">About BASE d.o.o.</a></li>
                <li><a href="#" className="text-[15px] text-[#9A9A9A] hover:text-[#FF6B58] hover:underline transition-colors cursor-pointer">Our Team</a></li>
                <li><a href="#" className="text-[15px] text-[#9A9A9A] hover:text-[#FF6B58] hover:underline transition-colors cursor-pointer">Careers</a></li>
                <li><a href="#" className="text-[15px] text-[#9A9A9A] hover:text-[#FF6B58] hover:underline transition-colors cursor-pointer">Press Kit</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg text-white font-semibold mb-6">Resources</h3>
              <ul className="space-y-4">
                <li><a href="#" className="text-[15px] text-[#9A9A9A] hover:text-[#FF6B58] hover:underline transition-colors cursor-pointer">Documentation</a></li>
                <li><a href="#" className="text-[15px] text-[#9A9A9A] hover:text-[#FF6B58] hover:underline transition-colors cursor-pointer">Support Center</a></li>
                <li><a href="#" className="text-[15px] text-[#9A9A9A] hover:text-[#FF6B58] hover:underline transition-colors cursor-pointer">Privacy Policy</a></li>
                <li><a href="#" className="text-[15px] text-[#9A9A9A] hover:text-[#FF6B58] hover:underline transition-colors cursor-pointer">Terms of Service</a></li>
              </ul>
            </div>
            <div>
              <p className="text-[22px] text-white font-serif mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>© BASE d.o.o. 2025</p>
              <p className="text-sm text-[#9A9A9A] mb-6">All rights reserved</p>
              <div className="space-y-3">
                <a href="#" className="text-sm text-[#9A9A9A] hover:text-[#FF6B58] underline block cursor-pointer">LinkedIn</a>
                <a href="#" className="text-sm text-[#9A9A9A] hover:text-[#FF6B58] underline block cursor-pointer">Twitter</a>
                <a href="#" className="text-sm text-[#9A9A9A] hover:text-[#FF6B58] underline block cursor-pointer">GitHub</a>
                <a href="#" className="text-sm text-[#9A9A9A] hover:text-[#FF6B58] underline block cursor-pointer">Medium</a>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-12 items-center mb-16">
            <div className="rounded-[20px] overflow-hidden h-60">
              <img src="https://readdy.ai/api/search-image?query=modern%20iOS%20devices%20displaying%20mobile%20applications%20in%20contemporary%20interior%20setting%2C%20iPhone%20and%20iPad%20showing%20VPN%20and%20cleaner%20app%20interfaces%2C%20minimalist%20product%20photography%2C%20soft%20natural%20lighting%2C%20clean%20white%20surfaces%2C%20professional%20technology%20showcase&width=500&height=300&seq=footer-product-scene-001&orientation=landscape" alt="Products" className="w-full h-full object-cover" />
            </div>
            <div className="px-8">
              <p className="text-[42px] leading-[1.3] text-white font-normal" style={{ fontFamily: 'Playfair Display, serif' }}>Security meets simplicity in every application we build</p>
            </div>
            <div>
              <p className="text-base text-[#9A9A9A] leading-relaxed mb-6">Get the latest news about our iOS applications and security updates</p>
              <form onSubmit={handleSubmit} data-readdy-form id="newsletter-form" className="flex gap-3">
                <input type="email" name="email" placeholder="Your email address" required className="flex-1 bg-transparent border border-[#4A4A4A] rounded-xl px-4 py-3 text-sm text-white placeholder-[#6B6B6B] focus:outline-none focus:border-[#FF6B58] transition-colors" />
                <button type="submit" className="bg-[#4A90E2] text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-[#3A7BC8] transition-all hover:scale-105 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                  Subscribe
                  <i className="ri-arrow-right-line"></i>
                </button>
              </form>
            </div>
          </div>

          <div className="flex items-center justify-center gap-12 pt-12 border-t border-[#4A4A4A]">
            <div className="flex items-center gap-2">
              <i className="ri-map-pin-line text-[#FF6B58]"></i>
              <span className="text-[15px] text-[#9A9A9A]">Ankaranska cesta 5</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-building-line text-[#FF6B58]"></i>
              <span className="text-[15px] text-[#9A9A9A]">Koper - Capodistria</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-mail-line text-[#FF6B58]"></i>
              <span className="text-[15px] text-[#9A9A9A]">6000</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-flag-line text-[#FF6B58]"></i>
              <span className="text-[15px] text-[#9A9A9A]">Slovenia</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-mail-send-line text-[#FF6B58]"></i>
              <a href="mailto:help@base-doo.com" className="text-[15px] text-[#9A9A9A] hover:text-[#FF6B58] hover:underline transition-colors cursor-pointer">help@base-doo.com</a>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-phone-line text-[#FF6B58]"></i>
              <a href="tel:+15077657175" className="text-[15px] text-[#9A9A9A] hover:text-[#FF6B58] transition-colors cursor-pointer">+1 5077657175</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}